// Adaptive Learning API Service
import { baseURL } from "./APIPath";

const backendUrl = baseURL;
const questionsUrl = process.env.REACT_APP_MASTER_API_URL || "http://localhost:8080";

/**
 * Adaptive Learning API Service
 * Handles all API calls for the adaptive learning feature
 */
const AdaptiveLearningApi = {
  /**
   * Get all concepts from Questions App
   * @param {string} subject - Subject code (e.g., "PHYSICS")
   * @param {string} token - Authorization token
   * @returns {Promise<Array>} List of concepts with concept units
   */
  getConcepts: async (subject, token) => {
    try {
      const questionAppToken = sessionStorage.getItem("app_masterdata_token");
      const response = await fetch(
        `${questionsUrl}/api/concepts/by-subjects?subjects=${subject}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Question-App-Token": questionAppToken || "",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch concepts: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching concepts:", error);
      throw error;
    }
  },

  /**
   * Get student progress for all concepts
   * @param {number} studentId - Student ID
   * @returns {Promise<Array>} List of concept progress
   */
  getProgress: async (studentId) => {
    try {
      const storedToken = sessionStorage.getItem("keycloak_access_token");
      const response = await fetch(
        `${backendUrl}/adaptive-learning/progress/${studentId}`,
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch progress: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching progress:", error);
      throw error;
    }
  },

  /**
   * Get student progress for a specific concept
   * @param {number} studentId - Student ID
   * @param {string} conceptCode - Concept code
   * @returns {Promise<Object>} Concept progress details
   */
  getConceptProgress: async (studentId, conceptCode) => {
    try {
      const storedToken = sessionStorage.getItem("keycloak_access_token");
      const response = await fetch(
        `${backendUrl}/adaptive-learning/progress/${studentId}/${conceptCode}`,
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch concept progress: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching concept progress:", error);
      throw error;
    }
  },

  /**
   * Start a new learning session
   * @param {string} conceptCode - Concept code
   * @param {number} studentId - Student ID
   * @param {number} numberOfQuestions - Number of questions (default 10)
   * @returns {Promise<Object>} Session details
   */
  startSession: async (conceptCode, studentId, numberOfQuestions = 10) => {
    try {
      const storedToken = sessionStorage.getItem("keycloak_access_token");
      const questionAppToken = sessionStorage.getItem("app_masterdata_token");
      const params = new URLSearchParams({
        conceptCode,
        studentId: studentId.toString(),
        numberOfQuestions: numberOfQuestions.toString(),
      });

      const response = await fetch(
        `${backendUrl}/adaptive-learning/sessions/start?${params}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${storedToken}`,
            "Content-Type": "application/json",
            "Question-App-Token": questionAppToken || "",
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || `Failed to start session: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error starting session:", error);
      throw error;
    }
  },

  /**
   * Get questions for a session (single attempt - no automatic retry)
   * @param {number} sessionId - Session ID
   * @returns {Promise<Array>} List of questions
   */
  getQuestions: async (sessionId) => {
    try {
      const storedToken = sessionStorage.getItem("keycloak_access_token");
      const questionAppToken = sessionStorage.getItem("app_masterdata_token");
      const response = await fetch(
        `${backendUrl}/adaptive-learning/sessions/${sessionId}/questions`,
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
            "Content-Type": "application/json",
            "Question-App-Token": questionAppToken || "",
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to fetch questions: ${response.status}`);
      }

      const questions = await response.json();
      
      // Check if questions array is not empty
      if (!questions || questions.length === 0) {
        throw new Error("Questions are still being generated. Please try again.");
      }

      return questions;
    } catch (error) {
      console.error("Error fetching questions:", error);
      throw error;
    }
  },

  /**
   * Submit answers for evaluation
   * @param {number} sessionId - Session ID
   * @param {number} studentId - Student ID
   * @param {string} conceptCode - Concept code
   * @param {Object} answers - Object with questionId as key and answer as value
   * @param {number} timeSpentSeconds - Time spent in seconds
   * @returns {Promise<Object>} Evaluation results
   */
  submitAnswers: async (
    sessionId,
    studentId,
    conceptCode,
    answers,
    timeSpentSeconds
  ) => {
    try {
      const storedToken = sessionStorage.getItem("keycloak_access_token");
      const payload = {
        sessionId,
        studentId,
        conceptCode,
        timeSpentSeconds,
        studentAnswers: answers,
      };

      const response = await fetch(
        `${backendUrl}/adaptive-learning/sessions/${sessionId}/submit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${storedToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorData = null;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          // Ignore parse error
        }

        const error = new Error(
          errorData?.message || errorText || `Failed to submit answers: ${response.status}`
        );
        error.response = {
          status: response.status,
          statusText: response.statusText,
          data: errorData,
        };
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting answers:", error);
      throw error;
    }
  },

  /**
   * Get all sessions for a student
   * @param {number} studentId - Student ID
   * @returns {Promise<Array>} List of sessions
   */
  getSessions: async (studentId) => {
    try {
      const storedToken = sessionStorage.getItem("keycloak_access_token");
      const response = await fetch(
        `${backendUrl}/adaptive-learning/sessions/student/${studentId}`,
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching sessions:", error);
      throw error;
    }
  },

  /**
   * Get session details
   * @param {number} sessionId - Session ID
   * @returns {Promise<Object>} Session details
   */
  getSession: async (sessionId) => {
    try {
      const storedToken = sessionStorage.getItem("keycloak_access_token");
      const response = await fetch(
        `${backendUrl}/adaptive-learning/sessions/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch session: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching session:", error);
      throw error;
    }
  },
};

export default AdaptiveLearningApi;

